import { useNavigate } from 'react-router-dom';

import { Badge, Center, Container, Group, Loader, Stack, Table, Text } from '@mantine/core';

import { LogoPaper, PageTitle } from '@/components';
import { formatWhen, MODE_CHIP, playbookPath } from '@/components/Playbooks/format';
import { usePlaybooks } from '@/hooks/usePlaybooks';

/**
 * Every playbook — what each client needs from a case, and the house
 * playbook they inherit (vNext-api `docs/playbooks.md`). A client playbook
 * is edited here and published as a new version with a reason; the house is
 * seeded from the repository and only read here. Cases keep the version
 * they started with.
 */
export function PlaybooksPage() {
  const navigate = useNavigate();
  const { data: playbooks, error, isLoading } = usePlaybooks();

  if (isLoading && !playbooks) {
    return (
      <Center h={300}>
        <Loader />
      </Center>
    );
  }
  if (error && !playbooks) {
    return (
      <Container size="xl" py="md">
        <Text c="dimmed">Not found.</Text>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack>
        <Stack gap={4}>
          <PageTitle title="Playbooks" />
          <Text size="sm" c="dimmed" maw={760}>
            What each client needs from a case — its instructions, scopes, standards and format, the
            report types it orders and the thresholds its cases run under — over the house playbook
            every client inherits. A new version is published with a reason; cases keep the version
            they started with.
          </Text>
        </Stack>

        <LogoPaper>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Playbook</Table.Th>
                <Table.Th>Mode</Table.Th>
                <Table.Th>Report types</Table.Th>
                <Table.Th>Budgets</Table.Th>
                <Table.Th>Current version</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(playbooks ?? []).map((playbook) => {
                const mode = playbook.mode ? MODE_CHIP[playbook.mode] : null;
                return (
                  <Table.Tr
                    key={playbook.key}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(playbookPath(playbook.key))}
                    data-testid={`playbook-row-${playbook.key}`}
                  >
                    <Table.Td>
                      <Group gap="xs">
                        <Text fw={500}>{playbook.name}</Text>
                        {playbook.kind === 'house' ? (
                          <Badge size="xs" variant="light">
                            House
                          </Badge>
                        ) : null}
                      </Group>
                      <Text size="xs" c="dimmed">
                        {playbook.key}
                        {playbook.clientCode ? ` · ${playbook.clientCode}` : ''}
                        {playbook.teams.length > 0
                          ? ` · ${playbook.teams.length} team${playbook.teams.length === 1 ? '' : 's'}`
                          : ''}
                        {playbook.configs.length > 0
                          ? ` · ${playbook.configs.length} config${playbook.configs.length === 1 ? '' : 's'}`
                          : ''}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {playbook.kind === 'client' && mode ? (
                        <Badge size="sm" color={mode.color} variant={mode.variant}>
                          {mode.label}
                        </Badge>
                      ) : (
                        <Text size="sm" c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{playbook.reportTypes.join(', ') || '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {playbook.budgetUsd !== null ? `$${playbook.budgetUsd}` : '—'}
                        {playbook.budgetBeforeStartUsd !== null
                          ? ` · $${playbook.budgetBeforeStartUsd} before Start`
                          : ''}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {playbook.version !== null ? (
                        <Stack gap={0}>
                          <Text size="sm">v{playbook.version}</Text>
                          <Text size="xs" c="dimmed">
                            {formatWhen(playbook.publishedAt)}
                            {playbook.publishedBy ? ` · ${playbook.publishedBy}` : ''}
                          </Text>
                        </Stack>
                      ) : (
                        <Text size="sm" c="dimmed">
                          not published
                        </Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </LogoPaper>
      </Stack>
    </Container>
  );
}
